import { useState } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '../../../components/ui/card';
import { DataTable, DataTableColumn } from '../../../components/ui/data-table';
import { Button } from '../../../components/ui/button';
import ActionDropdown from '../../../components/common/action-dropdown';
import InvestorCard from '../../../components/trading/InvestorCard';
import { Filter, Download, Users, DollarSign, TrendingUp } from 'lucide-react';
import PageHeader from '../../../components/common/PageHeader';
import TradingStatusBadge from '../../../components/trading/TradingStatusBadge';

type Investor = {
  id: string;
  name: string;
  email: string;
  investedAmount: number;
  currentProfit: number;
  roi: number;
  joinDate: string;
  status: 'active' | 'inactive' | 'pending';
  managerId: string;
  managerName: string;
  totalDeposits: number;
  totalWithdrawals: number;
};

const MAMInvestors = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState<'table' | 'grid'>('table');

  const investors: Investor[] = [
    {
      id: 'MINV001',
      name: 'Alice Williams',
      email: 'alice@example.com',
      investedAmount: 50000,
      currentProfit: 7500,
      roi: 15.0,
      joinDate: '2024-01-15',
      status: 'active',
      managerId: 'MGR001',
      managerName: 'John Smith',
      totalDeposits: 50000,
      totalWithdrawals: 0,
    },
    {
      id: 'MINV002',
      name: 'Charlie Brown',
      email: 'charlie@example.com',
      investedAmount: 30000,
      currentProfit: 4500,
      roi: 15.0,
      joinDate: '2024-01-10',
      status: 'active',
      managerId: 'MGR002',
      managerName: 'Jane Doe',
      totalDeposits: 30000,
      totalWithdrawals: 0,
    },
    {
      id: 'MINV003',
      name: 'Diana Miller',
      email: 'diana@example.com',
      investedAmount: 20000,
      currentProfit: -1000,
      roi: -5.0,
      joinDate: '2024-01-08',
      status: 'active',
      managerId: 'MGR003',
      managerName: 'Bob Johnson',
      totalDeposits: 20000,
      totalWithdrawals: 0,
    },
  ];

  const columns: DataTableColumn<Investor>[] = [
    {
      id: 'id',
      header: 'Investor ID',
      accessor: row => <span className="font-medium">{row.id}</span>,
      sortable: true,
      sortValue: row => row.id,
      width: '120px',
    },
    {
      id: 'name',
      header: 'Name',
      accessor: row => row.name,
      sortable: true,
      sortValue: row => row.name,
      width: '180px',
    },
    {
      id: 'email',
      header: 'Email',
      accessor: row => row.email,
      sortable: true,
      sortValue: row => row.email,
      width: '220px',
    },
    {
      id: 'managerName',
      header: 'Manager',
      accessor: row => row.managerName,
      sortable: true,
      sortValue: row => row.managerName,
      width: '180px',
    },
    {
      id: 'investedAmount',
      header: 'Invested',
      accessor: row => `$${row.investedAmount.toLocaleString()}`,
      sortable: true,
      sortValue: row => row.investedAmount,
      width: '120px',
    },
    {
      id: 'currentProfit',
      header: 'Current Profit',
      accessor: row => (
        <span
          className={
            row.currentProfit >= 0
              ? 'text-green-600 font-semibold'
              : 'text-red-600 font-semibold'
          }
        >
          ${row.currentProfit.toLocaleString()}
        </span>
      ),
      sortable: true,
      sortValue: row => row.currentProfit,
      width: '140px',
    },
    {
      id: 'roi',
      header: 'ROI',
      accessor: row => (
        <span
          className={
            row.roi >= 0
              ? 'text-green-600 font-semibold'
              : 'text-red-600 font-semibold'
          }
        >
          {row.roi.toFixed(2)}%
        </span>
      ),
      sortable: true,
      sortValue: row => row.roi,
      width: '100px',
    },
    {
      id: 'status',
      header: 'Status',
      accessor: row => <TradingStatusBadge status={row.status} />,
      sortable: true,
      sortValue: row => row.status,
      width: '120px',
    },
    {
      id: 'joinDate',
      header: 'Joined',
      accessor: row => new Date(row.joinDate).toLocaleDateString(),
      sortable: true,
      sortValue: row => row.joinDate,
      width: '120px',
    },
    {
      id: 'actions',
      header: 'Actions',
      accessor: row => (
        <ActionDropdown
          id={row.id}
          onView={() => console.log('View investor:', row.id)}
          onEdit={() => console.log('Edit investor:', row.id)}
          onDelete={() => console.log('Delete investor:', row.id)}
        />
      ),
      width: '120px',
    },
  ];

  const filteredInvestors = investors.filter(investor => {
    return (
      searchTerm === '' ||
      investor.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      investor.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      investor.id.toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  const totalInvested = investors.reduce(
    (sum, inv) => sum + inv.investedAmount,
    0
  );
  const totalProfit = investors.reduce(
    (sum, inv) => sum + inv.currentProfit,
    0
  );
  const activeInvestors = investors.filter(
    inv => inv.status === 'active'
  ).length;

  return (
    <div className="space-y-6">
      <div className="sticky top-0 z-20 bg-background pt-4">
        <PageHeader
          title="Investor Management"
          description="Manage MAM investors and their allocations"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="flex items-center gap-2 p-4 bg-blue-50 rounded-lg">
          <Users className="h-5 w-5 text-blue-600" />
          <div>
            <p className="text-sm text-muted-foreground">Total Investors</p>
            <p className="text-2xl font-bold text-blue-600">
              {investors.length}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 p-4 bg-green-50 rounded-lg">
          <Users className="h-5 w-5 text-green-600" />
          <div>
            <p className="text-sm text-muted-foreground">Active Investors</p>
            <p className="text-2xl font-bold text-green-600">
              {activeInvestors}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 p-4 bg-purple-50 rounded-lg">
          <DollarSign className="h-5 w-5 text-purple-600" />
          <div>
            <p className="text-sm text-muted-foreground">Total Invested</p>
            <p className="text-2xl font-bold text-purple-600">
              ${totalInvested.toLocaleString()}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 p-4 bg-orange-50 rounded-lg">
          <TrendingUp className="h-5 w-5 text-orange-600" />
          <div>
            <p className="text-sm text-muted-foreground">Total Profit</p>
            <p
              className={`text-2xl font-bold ${totalProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}
            >
              ${totalProfit.toLocaleString()}
            </p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button
            variant={viewMode === 'table' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('table')}
          >
            Table View
          </Button>
          <Button
            variant={viewMode === 'grid' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('grid')}
          >
            Grid View
          </Button>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm">
            <Filter className="h-4 w-4 mr-2" />
            Filters
          </Button>
          <Button variant="outline" size="sm">
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
        </div>
      </div>

      {viewMode === 'table' ? (
        <Card>
          <CardHeader>
            <CardTitle>All Investors</CardTitle>
            <CardDescription>
              Manage and monitor all MAM investors
            </CardDescription>
          </CardHeader>
          <CardContent>
            <DataTable
              columns={columns}
              data={filteredInvestors}
              searchPlaceholder="Search by name, email, or investor ID..."
              onSearchChange={setSearchTerm}
              showColumnVisibility={true}
              showExportButton={false}
              showAddButton={false}
            />
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredInvestors.map(investor => (
            <InvestorCard
              key={investor.id}
              id={investor.id}
              name={investor.name}
              email={investor.email}
              investedAmount={investor.investedAmount}
              currentProfit={investor.currentProfit}
              roi={investor.roi}
              joinDate={investor.joinDate}
              status={investor.status}
              onView={() => console.log('View:', investor.id)}
              onEdit={() => console.log('Edit:', investor.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default MAMInvestors;
